import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  LayoutDashboard,
  Settings,
  LogOut,
  Sparkles,
  Globe
} from 'lucide-react';

export const Sidebar = () => {
  const { user, logout, isAdmin } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
  ];

  if (isAdmin) {
    navItems.push({ path: '/admin', icon: Settings, label: t('nav.admin') });
  }

  return (
    <aside className="sidebar-container" data-testid="sidebar">
      {/* Logo */}
      <div className="p-4 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 bg-gradient-to-br from-indigo-600 to-violet-600 rounded-xl flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <span className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              WPLMS TTM
            </span>
            <p className="text-xs text-slate-500">Teaching-to-Model</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1">
        {navItems.map(item => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path ||
            (item.path === '/dashboard' && location.pathname.startsWith('/editor'));

          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`sidebar-link w-full ${isActive ? 'active' : ''}`}
              data-testid={`nav-${item.path.replace('/', '')}`}
            >
              <Icon className="w-5 h-5" />
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-slate-200 space-y-2">
        {/* Language Toggle */}
        <button
          onClick={toggleLanguage}
          className="sidebar-link w-full"
          data-testid="sidebar-language-toggle"
        >
          <Globe className="w-5 h-5" />
          <span>{language === 'zh' ? 'English' : '中文'}</span>
        </button>

        {/* User info */}
        {user && (
          <div className="px-3 py-2 text-sm">
            <p className="font-medium text-slate-900 truncate">{user?.username || 'User'}</p>
            <p className="text-slate-500 text-xs truncate">{user?.email || ''}</p>
          </div>
        )}

        {/* Logout */}
        <button
          onClick={handleLogout}
          className="sidebar-link w-full text-red-600 hover:bg-red-50 hover:text-red-700"
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5" />
          <span>{t('nav.logout')}</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
